import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "contexts/AuthContext";
import { fetchMemberWorkspaces, fetchWorkspace } from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import { switchUserAppRole } from "services/userRoleService";

const WorkspaceContext = createContext(null);

const GLOBAL_SELECTED_WORKSPACE_KEY = "selectedWorkspaceId";
const GLOBAL_SELECTED_WORKSPACE_READY_KEY = "selectedWorkspaceReady";
const storageKeyFor = (userId) => `selectedWorkspaceId:${userId || "anonymous"}`;
const workspaceUseStorageKeyFor = (workspaceId) => `selectedWorkspaceUse:${workspaceId || ""}`;
const mobileWorkspaceUseStorageKeyFor = (workspaceId) =>
  `olitask.selectedWorkspaceUse:${workspaceId || ""}`;

const accountModeOf = (workspace) =>
  String(workspace?.accountType || "").toUpperCase() === "ORGANIZATION" ||
  Boolean(workspace?.organizationId)
    ? "ORGANIZATION"
    : "INDIVIDUAL";

const defaultWorkspaceUse = (userRole) =>
  String(userRole || "").toUpperCase() === "HELPER" ? "HELPER" : "REQUESTER";

const workspaceUseOfIdentityWorkspace = (workspace) => {
  const value = String(workspace?.workspaceUse || "").toUpperCase();
  if (value === "HELPER") return "HELPER";
  if (value === "REQUESTER") return "REQUESTER";
  return null;
};

const normalizeWorkspaceUse = (value, workspace) => {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "HELPER" || normalized === "HIRING") return "HELPER";
  if (normalized === "REQUESTER" || normalized === "JOB_SEARCH") return "REQUESTER";
  return defaultWorkspaceUse(workspace?.currentAppRole);
};

const workModeOf = (workspaceUse) =>
  String(workspaceUse || "").toUpperCase() === "HELPER" ? "HELPER" : "REQUESTER";

const WorkspaceProvider = ({ children }) => {
  const { user, isAuthenticated, refreshAuth } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [selectedWorkspaceUse, setSelectedWorkspaceUseState] = useState("REQUESTER");
  const [workspaceUseLoading, setWorkspaceUseLoading] = useState(false);
  const [workspaceUseReady, setWorkspaceUseReady] = useState(false);
  const [workspaceListReady, setWorkspaceListReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const syncedRoleKeyRef = useRef("");

  const selectedWorkspace = useMemo(
    () =>
      workspaces.find((account) => account?.id === selectedWorkspaceId) ||
      null,
    [workspaces, selectedWorkspaceId],
  );

  const persistWorkspaceUse = useCallback((workspaceId, workspaceUse) => {
    if (!workspaceId || !workspaceUse) return;
    localStorage.setItem(workspaceUseStorageKeyFor(workspaceId), workspaceUse);
    localStorage.setItem(mobileWorkspaceUseStorageKeyFor(workspaceId), workspaceUse);
  }, []);

  const selectWorkspace = useCallback(
    async (accountId) => {
      const nextId = accountId || "";
      let nextWorkspace = workspaces.find((account) => account?.id === nextId);
      if (nextId && !nextWorkspace && actorId) {
        try {
          nextWorkspace = await fetchWorkspace(nextId, actorId);
          if (nextWorkspace?.id) {
            setWorkspaces((current) =>
              current.some((account) => account?.id === nextWorkspace.id)
                ? current
                : [nextWorkspace, ...current],
            );
          }
        } catch (fetchError) {
          console.warn("Selected workspace was not found locally or remotely:", fetchError);
        }
      }
      const identityWorkspaceUse = workspaceUseOfIdentityWorkspace(nextWorkspace);
      const cachedUse =
        localStorage.getItem(workspaceUseStorageKeyFor(nextId)) ||
        localStorage.getItem(mobileWorkspaceUseStorageKeyFor(nextId)) ||
        nextWorkspace?.workspaceUse;
      const nextUse = nextId
        ? identityWorkspaceUse || normalizeWorkspaceUse(cachedUse, nextWorkspace)
        : defaultWorkspaceUse(user?.currentAppRole);
      setSelectedWorkspaceId(nextId);
      setSelectedWorkspaceUseState(nextUse);

      if (actorId) {
        const key = storageKeyFor(actorId);
        if (nextId) {
          localStorage.setItem(key, nextId);
          localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_KEY, nextId);
          persistWorkspaceUse(nextId, nextUse);
        } else {
          localStorage.removeItem(key);
          localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
        }
        localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      }
    },
    [actorId, persistWorkspaceUse, user?.currentAppRole, workspaces],
  );

  const refreshWorkspaces = useCallback(async () => {
    if (!isAuthenticated || !actorId) {
      setWorkspaceListReady(false);
      setWorkspaces([]);
      setSelectedWorkspaceId("");
      setSelectedWorkspaceUseState(defaultWorkspaceUse(user?.currentAppRole));
      localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      return [];
    }

    try {
      setLoading(true);
      setWorkspaceListReady(false);
      setError(null);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "0");
      const page = await fetchMemberWorkspaces({
        actorId,
        page: 0,
        size: 100,
      });
      let accounts = page.content || [];
      const savedId =
        localStorage.getItem(storageKeyFor(actorId)) ||
        localStorage.getItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      if (savedId && !accounts.some((account) => account?.id === savedId)) {
        try {
          const remoteWorkspace = await fetchWorkspace(savedId, actorId);
          if (remoteWorkspace?.id) {
            accounts = [remoteWorkspace, ...accounts];
          }
        } catch (fetchError) {
          console.warn("Saved workspace was not found locally or remotely:", fetchError);
        }
      }
      setWorkspaces(accounts);

      const nextSelectedId =
        accounts.find((account) => account?.id === savedId)?.id ||
        accounts[0]?.id ||
        "";
      const nextWorkspace = accounts.find((account) => account?.id === nextSelectedId);
      const identityWorkspaceUse = workspaceUseOfIdentityWorkspace(nextWorkspace);
      const cachedUse =
        localStorage.getItem(workspaceUseStorageKeyFor(nextSelectedId)) ||
        localStorage.getItem(mobileWorkspaceUseStorageKeyFor(nextSelectedId));
      const nextUse = nextSelectedId
        ? identityWorkspaceUse || normalizeWorkspaceUse(cachedUse, nextWorkspace)
        : defaultWorkspaceUse(user?.currentAppRole);

      setSelectedWorkspaceId(nextSelectedId);
      setSelectedWorkspaceUseState(nextUse);
      if (nextSelectedId) {
        localStorage.setItem(storageKeyFor(actorId), nextSelectedId);
        localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_KEY, nextSelectedId);
        persistWorkspaceUse(nextSelectedId, nextUse);
      } else {
        localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      }
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      setWorkspaceListReady(true);

      return accounts;
    } catch (nextError) {
      setError(nextError);
      setWorkspaces([]);
      setSelectedWorkspaceId("");
      setSelectedWorkspaceUseState(defaultWorkspaceUse(user?.currentAppRole));
      localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      setWorkspaceListReady(true);
      return [];
    } finally {
      setLoading(false);
    }
  }, [actorId, isAuthenticated, persistWorkspaceUse, user?.currentAppRole]);

  useEffect(() => {
    refreshWorkspaces();
  }, [refreshWorkspaces]);

  useEffect(() => {
    let cancelled = false;

    const loadWorkspaceUse = async () => {
      setWorkspaceUseReady(false);
      setWorkspaceUseLoading(true);

      if (!selectedWorkspaceId || !selectedWorkspace) {
        setSelectedWorkspaceUseState(defaultWorkspaceUse(user?.currentAppRole));
        setWorkspaceUseLoading(false);
        setWorkspaceUseReady(true);
        return;
      }

      const identityWorkspaceUse = workspaceUseOfIdentityWorkspace(selectedWorkspace);
      if (identityWorkspaceUse) {
        setSelectedWorkspaceUseState(identityWorkspaceUse);
        persistWorkspaceUse(selectedWorkspaceId, identityWorkspaceUse);
        setWorkspaceUseLoading(false);
        setWorkspaceUseReady(true);
        return;
      }

      try {
        const record = await olreadiaService.workspaceUses.getForWorkspace(
          selectedWorkspaceId,
          actorId,
        );
        if (!cancelled) {
          setSelectedWorkspaceUseState(
            normalizeWorkspaceUse(record?.workspaceUse, selectedWorkspace),
          );
          persistWorkspaceUse(
            selectedWorkspaceId,
            normalizeWorkspaceUse(record?.workspaceUse, selectedWorkspace),
          );
        }
      } catch {
        if (!cancelled) {
          const cachedUse =
            localStorage.getItem(workspaceUseStorageKeyFor(selectedWorkspaceId)) ||
            localStorage.getItem(mobileWorkspaceUseStorageKeyFor(selectedWorkspaceId)) ||
            selectedWorkspace?.workspaceUse;
          setSelectedWorkspaceUseState(
            cachedUse
              ? normalizeWorkspaceUse(cachedUse, selectedWorkspace)
              : defaultWorkspaceUse(user?.currentAppRole),
          );
        }
      } finally {
        if (!cancelled) {
          setWorkspaceUseLoading(false);
          setWorkspaceUseReady(true);
        }
      }
    };

    loadWorkspaceUse();

    return () => {
      cancelled = true;
    };
  }, [actorId, persistWorkspaceUse, selectedWorkspace, selectedWorkspaceId, user?.currentAppRole]);

  useEffect(() => {
    if (!selectedWorkspaceId || !workspaceUseReady) return;
    const syncKey = `${selectedWorkspaceId}:${selectedWorkspaceUse}`;
    if (syncedRoleKeyRef.current === syncKey) return;
    syncedRoleKeyRef.current = syncKey;

    const syncSelectedWorkspaceRole = async () => {
      try {
        await switchUserAppRole(selectedWorkspaceUse);
        await refreshAuth?.();
      } catch (roleError) {
        console.warn("Failed to sync Olitask role context with backend:", roleError);
      }
    };

    syncSelectedWorkspaceRole();
  }, [refreshAuth, selectedWorkspaceId, selectedWorkspaceUse, workspaceUseReady]);

  const value = useMemo(
    () => ({
      workspaces,
      selectedWorkspace,
      selectedWorkspaceId,
      selectedWorkspaceUse,
      workspaceAccountMode: accountModeOf(selectedWorkspace),
      workspaceWorkMode: workModeOf(selectedWorkspaceUse),
      isOrganizationWorkspace:
        Boolean(selectedWorkspace) && accountModeOf(selectedWorkspace) === "ORGANIZATION",
      isIndividualWorkspace:
        Boolean(selectedWorkspace) && accountModeOf(selectedWorkspace) === "INDIVIDUAL",
      isEmployerWorkspace: workModeOf(selectedWorkspaceUse) === "HELPER",
      isProfessionalWorkspace: workModeOf(selectedWorkspaceUse) === "REQUESTER",
      isHelperWorkspace: workModeOf(selectedWorkspaceUse) === "HELPER",
      isRequesterWorkspace: workModeOf(selectedWorkspaceUse) === "REQUESTER",
      selectWorkspace,
      refreshWorkspaces,
      workspaceUseLoading,
      workspaceUseReady,
      workspaceListReady,
      loading,
      error,
    }),
    [
      error,
      loading,
      workspaces,
      refreshWorkspaces,
      selectWorkspace,
      selectedWorkspace,
      selectedWorkspaceId,
      selectedWorkspaceUse,
      workspaceUseLoading,
      workspaceUseReady,
      workspaceListReady,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return context;
};

export default WorkspaceProvider;
